import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef, inject, Input, OnInit, output, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { ProductCardWithQuantityComponent } from "@components/product-card-with-quantity/product-card-with-quantity.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { FgdsProductType, FullProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { RadioGroupComponent } from "@odx/angular/components/radio-group";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { OriginalRegard7000PlusConfiguration } from "@project/gas-warning-center-configuration/regard-7000-plus/original-regard-7000-plus-configuration";
import { BehaviorSubject, combineLatest, map, Observable, shareReplay, startWith, tap } from "rxjs";

@Component({
  selector: "app-gas-warning-center-module-selector-regard-7000",
  imports: [
    FormsModule,
    RadioGroupComponent,
    ReactiveFormsModule,
    AsyncPipe,
    UiKitModule,
    FilterProductComponent,
    ProductCardWithQuantityComponent,
    RefreshProductsHintComponent,
  ],
  templateUrl: "./module-selector-regard-7000.component.html",
  styleUrl: "./module-selector-regard-7000.component.scss",
  standalone: true,
})
export class ModuleSelectorRegard7000Component implements OnInit {
  private readonly productService = inject(ProductService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) project!: Project;

  @Input() set originalConfiguration(originalConfiguration: OriginalRegard7000PlusConfiguration | undefined) {
    if (!originalConfiguration) {
      return;
    }
    const inputModules = originalConfiguration.inputModules.filter((m) => m.product.isAvailable);
    if (inputModules.length) {
      this.inputModuleForm.patchValue({ inputModules });
    }
    const relayModules = originalConfiguration.relayModules.filter((m) => m.product.isAvailable);
    if (relayModules.length) {
      this.relayModuleForm.patchValue({ relayModules });
    }
    const gatewayModules = originalConfiguration.gatewayModules.filter((m) => m.product.isAvailable);
    if (gatewayModules.length) {
      this.gatewayModuleForm.patchValue({ gatewayModules });
    }
  }

  readonly moduleSelectionChanged = output<{
    inputModules: {
      product: Product;
      quantity: number;
    }[];
    relayModules: {
      product: Product;
      quantity: number;
    }[];
    gatewayModules: {
      product: Product;
      quantity: number;
    }[];
  }>();

  protected inputModuleForm = this.formBuilder.group({
    inputModules: new FormControl<ProductWithQuantity[]>([]),
  });

  protected relayModuleForm = this.formBuilder.group({
    relayModules: new FormControl<ProductWithQuantity[]>([]),
  });

  protected gatewayModuleForm = this.formBuilder.group({
    gatewayModules: new FormControl<ProductWithQuantity[]>([]),
  });

  protected inputModules$!: Observable<Product[]>;
  protected relayModules$!: Observable<Product[]>;
  protected gatewayModules$!: Observable<Product[]>;

  protected readonly filteredInputModules = signal<Product[]>([]);
  protected readonly filteredRelayModules = signal<Product[]>([]);
  protected readonly filteredGatewayModules = signal<Product[]>([]);

  private readonly searchValue$ = new BehaviorSubject<string | null>(null);

  constructor() {
    combineLatest([
      this.inputModuleForm.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        map((formValue) => formValue.inputModules ?? []),
        startWith(this.inputModuleForm.value.inputModules ?? []),
      ),

      this.relayModuleForm.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        map((formValue) => formValue.relayModules ?? []),
        startWith(this.relayModuleForm.value.relayModules ?? []),
      ),

      this.gatewayModuleForm.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        map((formValue) => formValue.gatewayModules ?? []),
        startWith(this.gatewayModuleForm.value.gatewayModules ?? []),
      ),
    ])
      .pipe(
        map(([inputModules, relayModules, gatewayModules]) => ({
          inputModules,
          relayModules,
          gatewayModules,
        })),
        tap((moduleSelection) => this.moduleSelectionChanged.emit(moduleSelection)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.collectModules();
    this.handleSearchValueChange();
  }

  protected search(value: string) {
    this.searchValue$.next(value);
  }

  protected getSelectedQuantity(formValue: ProductWithQuantity[] | null | undefined, product: Product) {
    if (!formValue) {
      return 0;
    }
    const selectedProduct = formValue.find((formProduct: ProductWithQuantity) => formProduct.product.id === product.id);
    return selectedProduct?.quantity ?? 0;
  }

  protected updateSelectedInputModules(quantity: number, product: Product) {
    const currentSelectedProducts = this.inputModuleForm.value.inputModules;
    if (!currentSelectedProducts) {
      return;
    }
    const updatedSelectedProducts = this.updateSelectedProducts(currentSelectedProducts, product, quantity);
    this.inputModuleForm.patchValue({ inputModules: updatedSelectedProducts });
  }

  protected updateSelectedRelayModules(quantity: number, product: Product) {
    const currentSelectedProducts = this.relayModuleForm.value.relayModules;
    if (!currentSelectedProducts) {
      return;
    }
    const updatedSelectedProducts = this.updateSelectedProducts(currentSelectedProducts, product, quantity);
    this.relayModuleForm.patchValue({ relayModules: updatedSelectedProducts });
  }

  protected updateSelectedGatewayModules(quantity: number, product: Product) {
    const currentSelectedProducts = this.gatewayModuleForm.value.gatewayModules;
    if (!currentSelectedProducts) {
      return;
    }
    const updatedSelectedProducts = this.updateSelectedProducts(currentSelectedProducts, product, quantity);
    this.gatewayModuleForm.patchValue({ gatewayModules: updatedSelectedProducts });
  }

  private updateSelectedProducts(
    currentSelectedProducts: {
      product: Product;
      quantity: number;
    }[],
    product: Product,
    quantity: number,
  ) {
    const updatedSelectedProducts = currentSelectedProducts.filter(
      (selectedProduct) => selectedProduct.product.id !== product.id,
    );
    if (quantity > 0) {
      updatedSelectedProducts.push({ product, quantity });
    }
    return updatedSelectedProducts;
  }

  private collectModules() {
    const products$ = this.productService
      .getAvailableProducts(FgdsProductType.GASWARNINGCENTER)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.inputModules$ = products$.pipe(
      map((products) =>
        products.filter((p) =>
          [
            FullProductType.GASWARNINGCENTER_INPUT_ANALOG,
            FullProductType.GASWARNINGCENTER_INPUT_DIGITAL,
            FullProductType.GASWARNINGCENTER_INPUT_BUS,
          ].includes(p.fullType),
        ),
      ),
    );

    this.relayModules$ = products$.pipe(
      map((products) =>
        products.filter((p) =>
          [FullProductType.GASWARNINGCENTER_RELAYS_24V, FullProductType.GASWARNINGCENTER_RELAYS_240V].includes(p.fullType),
        ),
      ),
    );

    this.gatewayModules$ = products$.pipe(
      map((products) => products.filter((p) => p.fullType === FullProductType.GASWARNINGCENTER_GATEWAY)),
    );
  }

  private handleSearchValueChange() {
    combineLatest([this.searchValue$, this.inputModules$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([searchValue, inputModules]) => {
        if (!inputModules) {
          this.filteredInputModules.set([]);
          return;
        }
        if (!searchValue) {
          this.filteredInputModules.set(inputModules);
          return;
        }
        this.filteredInputModules.set(inputModules.filter((product) => this.productService.searchFilter(product, searchValue)));
      });
    combineLatest([this.searchValue$, this.relayModules$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([searchValue, relayModules]) => {
        if (!relayModules) {
          this.filteredRelayModules.set([]);
          return;
        }
        if (!searchValue) {
          this.filteredRelayModules.set(relayModules);
          return;
        }
        this.filteredRelayModules.set(relayModules.filter((product) => this.productService.searchFilter(product, searchValue)));
      });
    combineLatest([this.searchValue$, this.gatewayModules$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([searchValue, gatewayModules]) => {
        if (!gatewayModules) {
          this.filteredGatewayModules.set([]);
          return;
        }
        if (!searchValue) {
          this.filteredGatewayModules.set(gatewayModules);
          return;
        }
        this.filteredGatewayModules.set(
          gatewayModules.filter((product) => this.productService.searchFilter(product, searchValue)),
        );
      });
  }
}
